/**
 * GameAudio — Stage 6 WebAudio manager for Kicky Road.
 *
 * Direct WebAudio (no Howler, zero deps). Lazily creates the AudioContext on
 * the first user gesture (the same title→playing gesture that starts the
 * run), loads + decodes all six audio files in parallel (graceful on failure — the
 * game runs silent), and wires sim events to sound:
 *
 *   - music_loop.wav : looping bed, ducked on crash, restored on restart.
 *   - wind.wav       : loop, playbackRate + gain scaled by speed.
 *   - engine_whoosh  : one-shot per threat pass, pan by vehicle x, throttled.
 *   - kick_impact    : on `kick` (shaped envelope) + `kickWhiff` (quiet/fast).
 *   - crash.wav      : on `crash`, full gain.
 *   - coin_pickup    : on `coin`, one BufferSource per pickup (polyphonic)
 *                      with a rising semitone ladder across a chain.
 *
 * `update(state, dt)` is called each render frame from main.ts: it drives the
 * wind rate/gain, music duck/restore (by reading sim phase), and whoosh
 * pass-detection (tracking each threat vehicle's z crossing 0).
 *
 * Zero per-frame allocation in steady state: the per-vehicle z-tracking map is
 * keyed by pool slot id and reused; envelopes use setValueAtTime/exponential
 * ramp on a recycled gain node; the whoosh source list is a fixed pool.
 */

import type { WorldState, VehicleEntity } from "../sim/types";
import { TUNING } from "../sim/tuning";
import { AUDIO } from "./audioConfig";
import type { EventDispatcher, JuiceHandlers } from "./events";

const SPEED_MAX = TUNING.player.speedMax;

/** Decoded audio buffers keyed by role. `null` = load failed (run silent). */
interface AudioBuffers {
  music: AudioBuffer | null;
  wind: AudioBuffer | null;
  whoosh: AudioBuffer | null;
  kick: AudioBuffer | null;
  crash: AudioBuffer | null;
  coin: AudioBuffer | null;
}

/**
 * Coin-chain ladder step for the next pickup.
 *
 * Pure so the ladder can be unit-tested without WebAudio. `prevStep` is the
 * step used by the previous pickup (-1 = no chain yet), `gapSec` the time
 * since it. A gap longer than `AUDIO.coin.chainResetSec` restarts at 0;
 * otherwise the ladder climbs by one, clamped to `AUDIO.coin.maxStep`.
 */
export function coinChainStep(prevStep: number, gapSec: number): number {
  if (prevStep < 0 || gapSec > AUDIO.coin.chainResetSec || !Number.isFinite(gapSec)) {
    return 0;
  }
  const next = prevStep + 1;
  return next > AUDIO.coin.maxStep ? AUDIO.coin.maxStep : next;
}

/** playbackRate for a coin-chain step (equal-temperament semitone ladder). */
export function coinPlaybackRate(step: number): number {
  return Math.pow(2, (step * AUDIO.coin.semitonesPerStep) / 12);
}

/** A playing whoosh source bookkeeping entry. */
interface WhooshVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  // wall-clock seconds the source stops at (for GC in update()).
  stopAt: number;
}

/** A playing coin chime source. */
interface CoinVoice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  stopAt: number;
}

export class GameAudio {
  private ctx: AudioContext | null = null;
  private buffers: AudioBuffers = {
    music: null,
    wind: null,
    whoosh: null,
    kick: null,
    crash: null,
    coin: null,
  };
  private loaded = false;

  // --- Persistent nodes (created once the context exists) ------------------
  private musicSource: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  private windSource: AudioBufferSourceNode | null = null;
  private windGain: GainNode | null = null;
  private masterGain: GainNode | null = null;

  // --- Music duck state ----------------------------------------------------
  private musicDucked = false;

  // --- Whoosh bookkeeping --------------------------------------------------
  private readonly activeWhooshes: WhooshVoice[] = [];
  // Per-vehicle (by pool slot id) previous z, for pass-detection. Reused.
  private readonly prevZ: Map<number, number> = new Map();
  // Track whether prevZ was fully cleared when no threats are live, to avoid
  // re-clearing every frame.
  private cleared = false;

  // --- Coin chain state ----------------------------------------------------
  /** Ladder step used by the previous pickup; -1 = no chain in progress. */
  private coinStep = -1;
  /** ctx.currentTime of the previous pickup. */
  private coinLastTime = 0;
  /** Live coin voices (for the maxVoices cap; oldest is stolen when full). */
  private readonly activeCoins: CoinVoice[] = [];

  // --- Gesture unlock ------------------------------------------------------
  private unlockListener: ((ev: Event) => void) | null = null;
  private unlocked = false;

  /**
   * Register a one-time pointerdown + keydown listener that unlocks audio on
   * the first user gesture (the title→playing gesture). Safe to call at boot;
   * the context is created lazily inside the handler.
   */
  registerUnlock(): void {
    if (this.unlockListener) return;
    const handler = (ev: Event): void => {
      void ev;
      this.unlock();
    };
    this.unlockListener = handler;
    window.addEventListener("pointerdown", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
  }

  /**
   * Explicitly unlock audio (e.g. from the title start button). Creates the
   * AudioContext if needed, resumes it, starts the music + wind beds, and
   * removes the gesture listeners. Idempotent.
   */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    // Remove the gesture listeners so a later explicit call is a no-op.
    if (this.unlockListener) {
      window.removeEventListener("pointerdown", this.unlockListener);
      window.removeEventListener("keydown", this.unlockListener);
      this.unlockListener = null;
    }
    void this.startBeds();
  }

  /**
   * Load + decode all six audio files in parallel. Graceful on failure: any
   * rejected decode leaves that buffer `null` and the game runs without it.
   * Safe to call before or after `unlock()`; beds start once both are done.
   */
  async loadAll(): Promise<void> {
    const entries = await Promise.all([
      this.loadDecode(AUDIO.paths.music),
      this.loadDecode(AUDIO.paths.wind),
      this.loadDecode(AUDIO.paths.engineWhoosh),
      this.loadDecode(AUDIO.paths.kick),
      this.loadDecode(AUDIO.paths.crash),
      this.loadDecode(AUDIO.paths.coin),
    ]);
    this.buffers.music = entries[0];
    this.buffers.wind = entries[1];
    this.buffers.whoosh = entries[2];
    this.buffers.kick = entries[3];
    this.buffers.crash = entries[4];
    this.buffers.coin = entries[5];
    this.loaded = true;
    // If the context was already unlocked, start the beds now.
    if (this.ctx) {
      void this.startBeds();
    }
  }

  /**
   * Register event handlers on the dispatcher for kick / kickWhiff / crash /
   * coin.
   * The dispatcher is the single fan-out point — this does NOT poll sim
   * internals for discrete events.
   */
  register(dispatcher: EventDispatcher): void {
    const handlers: JuiceHandlers = {
      onKick: (_vehicleId, _leg, _contactX, _contactZ) => {
        this.playKick(false);
      },
      onKickWhiff: () => {
        this.playKick(true);
      },
      onCrash: (_vehicleId) => {
        this.playCrash();
      },
      onCoin: (_coinId, _x, _z, _value) => {
        this.playCoin();
      },
    };
    dispatcher.add(handlers);
  }

  /**
   * Per-frame update. Drives wind rate/gain, music duck/restore (by sim
   * phase), and whoosh pass-detection across the threat vehicle pool.
   */
  update(state: WorldState, dt: number): void {
    void dt;
    if (!this.ctx || !this.unlocked) return;

    // --- Wind: rate + gain scaled by forwardSpeed/speedMax ----------------
    if (this.windSource && this.windGain) {
      const frac = SPEED_MAX > 0 ? state.player.forwardSpeed / SPEED_MAX : 0;
      const f = frac < 0 ? 0 : frac > 1 ? 1 : frac;
      const rate = AUDIO.wind.rateMin + (AUDIO.wind.rateMax - AUDIO.wind.rateMin) * f;
      const gain = AUDIO.wind.gainMin + (AUDIO.wind.gainMax - AUDIO.wind.gainMin) * f;
      const t = this.ctx.currentTime;
      // setTargetAtTime for zipper-free ramps (avoids per-frame clicks).
      this.windSource.playbackRate.setTargetAtTime(rate, t, 0.1);
      this.windGain.gain.setTargetAtTime(gain, t, 0.1);
    }

    // --- Music duck/restore by sim phase ----------------------------------
    // Duck while crashed/gameover; restore when back to playing.
    const wantDuck = state.phase === "crashed" || state.phase === "gameover";
    if (wantDuck !== this.musicDucked && this.musicGain) {
      this.musicDucked = wantDuck;
      const t = this.ctx.currentTime;
      const target = wantDuck ? AUDIO.music.duckGain : AUDIO.music.gain;
      this.musicGain.gain.setTargetAtTime(target, t, 0.15);
    }

    // --- Whoosh: detect threat vehicles crossing z=0 (+Z travel) ----------
    this.detectWhooshes(state);

    // --- Reap finished whoosh + coin voices -------------------------------
    this.reapWhooshes();
    this.reapCoins();
  }

  // -------------------------------------------------------------------------
  // Internal: loading + decoding
  // -------------------------------------------------------------------------

  private async loadDecode(path: string): Promise<AudioBuffer | null> {
    try {
      const res = await fetch(path);
      if (!res.ok) {
        console.warn(`Audio: ${path} returned HTTP ${res.status} — running silent.`);
        return null;
      }
      const arr = await res.arrayBuffer();
      // decodeAudioData needs a context; create a transient one if the
      // gesture hasn't fired yet so loading can proceed pre-unlock.
      const ctx = this.ctx ?? this.ensureContext();
      return await ctx.decodeAudioData(arr);
    } catch (err) {
      console.warn(`Audio: failed to load/decode ${path}:`, err);
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // Internal: context + bed management
  // -------------------------------------------------------------------------

  private ensureContext(): AudioContext {
    if (this.ctx) return this.ctx;
    // webkitAudioContext fallback for older Safari.
    const Ctor: typeof AudioContext =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    this.ctx = ctx;
    // Master gain at unity; per-bed gains do the mixing.
    // ?mute=1 silences everything (dev/testing convenience). Pages can also
    // hard-mute unconditionally by setting `window.__FORCE_MUTE = true`
    // BEFORE the first sound plays — perfbench.html does, so an automated
    // bench can never leak audio whatever URL it was loaded with.
    const muted =
      new URLSearchParams(window.location.search).has("mute") ||
      (window as unknown as { __FORCE_MUTE?: boolean }).__FORCE_MUTE === true;
    const master = ctx.createGain();
    master.gain.value = muted ? 0 : 1;
    master.connect(ctx.destination);
    this.masterGain = master;
    return ctx;
  }

  /**
   * Start the music + wind looping beds. Requires the context to exist
   * (called from unlock() after the gesture, or from loadAll() if the
   * context was already created for decoding).
   */
  private async startBeds(): Promise<void> {
    if (!this.ctx) {
      // No context yet — create it now (this is the unlock path).
      this.ensureContext();
    }
    const ctx = this.ctx;
    if (!ctx || !this.loaded) {
      // Either no context or buffers not loaded yet; the other side will
      // call startBeds() again when its prerequisite completes.
      return;
    }
    // Resume in case the context is suspended (autoplay policy).
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        // Resume can reject if the page isn't focused; beds will start on
        // the next focus/gesture. Non-fatal.
      }
    }
    this.startMusic(ctx);
    this.startWind(ctx);
  }

  private startMusic(ctx: AudioContext): void {
    if (this.musicSource || !this.buffers.music) return;
    const src = ctx.createBufferSource();
    src.buffer = this.buffers.music;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = this.musicDucked ? AUDIO.music.duckGain : AUDIO.music.gain;
    src.connect(gain).connect(this.masterGain ?? ctx.destination);
    src.start();
    this.musicSource = src;
    this.musicGain = gain;
  }

  private startWind(ctx: AudioContext): void {
    if (this.windSource || !this.buffers.wind) return;
    const src = ctx.createBufferSource();
    src.buffer = this.buffers.wind;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = AUDIO.wind.gainMin;
    src.playbackRate.value = AUDIO.wind.rateMin;
    src.connect(gain).connect(this.masterGain ?? ctx.destination);
    src.start();
    this.windSource = src;
    this.windGain = gain;
  }

  // -------------------------------------------------------------------------
  // Internal: one-shot SFX
  // -------------------------------------------------------------------------

  /** Play the kick sample. Whiff = quieter + faster playbackRate. */
  private playKick(whiff: boolean): void {
    const ctx = this.ctx;
    const buf = this.buffers.kick;
    if (!ctx || !buf || !this.unlocked) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = false;
    if (whiff) {
      src.playbackRate.value = AUDIO.kick.whiffRate;
    }
    const gain = ctx.createGain();
    // Envelope: attack 5 ms to peak, exponential decay to 30% by 250 ms.
    // The file is crunch-sustain, so we shape the sustain down per the
    // ASSET_PIPELINE note. Whiff uses whiffGain as the peak.
    const peak = whiff ? AUDIO.kick.whiffGain : 1.0;
    const t = ctx.currentTime;
    const attackT = t + AUDIO.kick.attackMs / 1000;
    const decayT = t + AUDIO.kick.decayMs / 1000;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(peak, attackT);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, peak * AUDIO.kick.decayTargetFrac),
      decayT,
    );
    src.connect(gain).connect(this.masterGain ?? ctx.destination);
    src.start();
    // Stop a bit after the decay to free the voice.
    src.stop(t + buf.duration + 0.05);
  }

  /**
   * Play the coin chime for one pickup.
   *
   * POLYPHONIC: a fresh BufferSource per pickup, so a 6-10 coin chain in
   * ~1.5 s overlaps naturally instead of cutting itself off (only the
   * `maxVoices` cap steals, and it steals the OLDEST voice with a short
   * release so it can't click).
   *
   * PITCH LADDER: consecutive pickups within `chainResetSec` climb
   * `semitonesPerStep` semitones each (capped at `maxStep`); a gap longer
   * than `chainResetSec` restarts the ladder at the root note.
   */
  private playCoin(): void {
    const ctx = this.ctx;
    const buf = this.buffers.coin;
    if (!ctx || !buf || !this.unlocked) return;

    const t = ctx.currentTime;
    this.coinStep = coinChainStep(this.coinStep, t - this.coinLastTime);
    this.coinLastTime = t;

    // Voice cap: steal the oldest voice (short release, then stop).
    if (this.activeCoins.length >= AUDIO.coin.maxVoices) {
      const oldest = this.activeCoins.shift();
      if (oldest) {
        try {
          oldest.gain.gain.cancelScheduledValues(t);
          oldest.gain.gain.setValueAtTime(oldest.gain.gain.value, t);
          oldest.gain.gain.linearRampToValueAtTime(0.0001, t + AUDIO.coin.releaseSec);
          oldest.source.stop(t + AUDIO.coin.releaseSec);
        } catch {
          /* already stopped */
        }
      }
    }

    const rate = coinPlaybackRate(this.coinStep);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = false;
    src.playbackRate.value = rate;

    const gain = ctx.createGain();
    gain.gain.value = AUDIO.coin.gain;
    src.connect(gain).connect(this.masterGain ?? ctx.destination);

    // playbackRate shortens the sample: real length = duration / rate.
    const playLen = buf.duration / rate;
    src.start();
    src.stop(t + playLen + 0.02);
    this.activeCoins.push({ source: src, gain, stopAt: t + playLen + 0.05 });
  }

  /** Drop coin voices whose source has finished (keeps the cap meaningful). */
  private reapCoins(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    let w = 0;
    for (let r = 0; r < this.activeCoins.length; r++) {
      const voice = this.activeCoins[r];
      if (now < voice.stopAt) {
        this.activeCoins[w++] = voice;
      }
    }
    this.activeCoins.length = w;
  }

  /** Play the crash sample at full gain. */
  private playCrash(): void {
    const ctx = this.ctx;
    const buf = this.buffers.crash;
    if (!ctx || !buf || !this.unlocked) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = false;
    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    src.connect(gain).connect(this.masterGain ?? ctx.destination);
    src.start();
    src.stop(ctx.currentTime + buf.duration + 0.05);
  }

  // -------------------------------------------------------------------------
  // Internal: whoosh pass-detection + throttling
  // -------------------------------------------------------------------------

  /**
   * Detect threat vehicles crossing z=0 from <0 to >=0 (near-miss OR contact).
   * Plays engine_whoosh with volume scaled by 1/min(|laneGap|,3) and pan by
   * the vehicle's x sign. Rate-limited to maxConcurrent simultaneous sources;
   * skipped if more than skipThreshold are already playing.
   */
  private detectWhooshes(state: WorldState): void {
    const ctx = this.ctx;
    const buf = this.buffers.whoosh;
    if (!ctx || !buf || !this.unlocked) return;

    const vehicles = state.vehicles;
    const liveIds = new Set<number>();

    for (let i = 0; i < vehicles.length; i++) {
      const v = vehicles[i];
      // Only driving threats generate a pass whoosh. Kicked vehicles are
      // no longer approaching; ambient lanes are beyond the rails.
      if (v.role !== "threat" || v.motion !== "driving") continue;
      liveIds.add(v.id);
      const prev = this.prevZ.get(v.id);
      if (prev === undefined) {
        // First sighting — seed without firing (avoids a spurious whoosh
        // for vehicles that spawned already past z=0).
        this.prevZ.set(v.id, v.z);
        continue;
      }
      // Crossing from <0 to >=0 (player at z=0, world scrolls +Z).
      if (prev < 0 && v.z >= 0) {
        this.fireWhoosh(v);
      }
      this.prevZ.set(v.id, v.z);
    }

    // Drop stale ids (recycled pool slots) so prevZ doesn't grow unbounded.
    if (liveIds.size > 0) {
      for (const id of [...this.prevZ.keys()]) {
        if (!liveIds.has(id)) this.prevZ.delete(id);
      }
      this.cleared = false;
    } else if (!this.cleared) {
      // No live threats at all — clear everything once.
      this.prevZ.clear();
      this.cleared = true;
    }
  }

  private fireWhoosh(v: VehicleEntity): void {
    const ctx = this.ctx;
    const buf = this.buffers.whoosh;
    if (!ctx || !buf) return;
    // Throttle: skip if more than skipThreshold currently playing.
    if (this.activeWhooshes.length > AUDIO.whoosh.skipThreshold) return;
    // Hard cap.
    if (this.activeWhooshes.length >= AUDIO.whoosh.maxConcurrent) return;

    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = false;

    // Volume scaled by 1/min(|laneGap|,3). laneGap ~ |v.x - player.x|; we
    // approximate lateral gap by |v.x| since the player is fixed at x≈0.
    const laneGap = Math.min(Math.abs(v.x), 3);
    const gapGain = laneGap > 0 ? 1 / laneGap : 1;
    const vol = AUDIO.whoosh.baseGain * Math.min(gapGain, 1);
    const gain = ctx.createGain();
    gain.gain.value = vol;

    // Stereo pan by vehicle x sign.
    const panner = ctx.createStereoPanner();
    panner.pan.value = v.x < 0 ? -1 : v.x > 0 ? 1 : 0;

    src.connect(gain).connect(panner).connect(this.masterGain ?? ctx.destination);
    const t = ctx.currentTime;
    src.start();
    src.stop(t + buf.duration + 0.05);
    this.activeWhooshes.push({
      source: src,
      gain,
      stopAt: t + buf.duration + 0.1,
    });
  }

  private reapWhooshes(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    // Filter in place to avoid allocating a new array each frame.
    let w = 0;
    for (let r = 0; r < this.activeWhooshes.length; r++) {
      const voice = this.activeWhooshes[r];
      if (now < voice.stopAt) {
        this.activeWhooshes[w++] = voice;
      }
    }
    this.activeWhooshes.length = w;
  }

  // -------------------------------------------------------------------------
  // Lifecycle (optional — for completeness; not currently called by main.ts)
  // -------------------------------------------------------------------------

  /** Stop all sound and close the context. */
  dispose(): void {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        /* already stopped */
      }
      this.musicSource = null;
    }
    if (this.windSource) {
      try {
        this.windSource.stop();
      } catch {
        /* already stopped */
      }
      this.windSource = null;
    }
    for (const voice of this.activeWhooshes) {
      try {
        voice.source.stop();
      } catch {
        /* already stopped */
      }
    }
    this.activeWhooshes.length = 0;
    for (const voice of this.activeCoins) {
      try {
        voice.source.stop();
      } catch {
        /* already stopped */
      }
    }
    this.activeCoins.length = 0;
    this.coinStep = -1;
    this.coinLastTime = 0;
    this.prevZ.clear();
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
    }
  }
}
